/** Legal Calendar — Agenda view (Dean, 8 Oct 2026). Overdue / Today / This week / Later, grouped by owner, full labels. */
import { useMemo, useState } from "react";
import { Calendar as CalIcon, ExternalLink, ChevronDown, ChevronRight, Landmark, UserRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import RediariseDialog from "./RediariseDialog";
import { OwnerChip } from "./OwnerChip";
import { EventActions } from "./EventActions";
import { TONE_CLASS, PriorityDot, courtSummary } from "./legalPriority";

const SECTIONS = [
  { key: "overdue", label: "Overdue",   tone: "text-red-800 bg-red-50 border-red-200" },
  { key: "today",   label: "Today",     tone: "text-amber-900 bg-amber-50 border-amber-200" },
  { key: "week",    label: "This week", tone: "text-sky-900 bg-sky-50 border-sky-200" },
  { key: "later",   label: "Later",     tone: "text-slate-700 bg-slate-50 border-slate-200" },
];

const isoToday = () => new Date().toISOString().slice(0, 10);
const PAGE = 20;
const addDays = (iso, n) => { const d = new Date(iso + "T00:00:00"); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const dayLabel = (iso) => new Date(iso + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });

export function bucketEvents(events, today = isoToday()) {
  const weekEnd = addDays(today, 7);
  const out = { overdue: [], today: [], week: [], later: [] };
  for (const ev of events) {
    if (!ev.date) continue;
    const key = ev.date < today ? "overdue" : ev.date === today ? "today" : ev.date <= weekEnd ? "week" : "later";
    out[key].push(ev);
  }
  for (const k of Object.keys(out)) out[k].sort((a, b) => (a.date || "").localeCompare(b.date || "") || (a.label || "").localeCompare(b.label || ""));
  return out;
}

export function groupByOwner(list) {
  const m = new Map();
  for (const ev of list) {
    const owner = ev.owner || "Unassigned";
    if (!m.has(owner)) m.set(owner, []);
    m.get(owner).push(ev);
  }
  return [...m.entries()].sort((a, b) => (a[0] === "Unassigned") - (b[0] === "Unassigned") || a[0].localeCompare(b[0]));
}

export default function LegalAgenda({ events, loading, onOpenCase, onChanged }) {
  const [collapsed, setCollapsed] = useState(() => new Set());
  const buckets = useMemo(() => bucketEvents(events), [events]);
  const toggle = (k) => setCollapsed((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const total = events.length;
  return (
    <div className="border rounded-lg bg-white" data-testid="lcal-agenda">
      {total === 0 && !loading && <div className="p-8 text-center text-sm text-muted-foreground" data-testid="lcal-agenda-empty">Nothing in the diary for this view.</div>}
      {SECTIONS.map((s) => {
        const list = buckets[s.key];
        if (!list.length) return null;
        const open = !collapsed.has(s.key);
        return (
          <div key={s.key} data-testid={`lcal-agenda-section-${s.key}`}>
            <button type="button" onClick={() => toggle(s.key)} className={`w-full flex items-center gap-2 px-3 py-1.5 border-b text-xs font-semibold uppercase tracking-wide ${s.tone}`} data-testid={`lcal-agenda-toggle-${s.key}`}>
              {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              {s.label}
              <span className="ml-1 text-[11px] font-bold tabular-nums px-1.5 rounded bg-white/70 border" data-testid={`lcal-agenda-count-${s.key}`}>{list.length}</span>
            </button>
            {open && groupByOwner(list).map(([owner, evs]) => (
              <OwnerGroup key={owner} owner={owner} evs={evs} section={s.key} onOpenCase={onOpenCase} onChanged={onChanged} />
            ))}
          </div>
        );
      })}
    </div>
  );
}

function OwnerGroup({ owner, evs, section, onOpenCase, onChanged }) {
  const [showAll, setShowAll] = useState(false);
  const shown = showAll ? evs : evs.slice(0, PAGE);
  return (
    <div data-testid={`lcal-agenda-owner-${section}-${owner}`}>
      <div className="px-3 py-1 bg-slate-50/70 border-b text-[10px] uppercase tracking-wide text-slate-500 flex items-center gap-1">
        <UserRound className="w-3 h-3" /> {owner} <span className="text-slate-400">· {evs.length}</span>
      </div>
      <ul className="divide-y">
        {shown.map((ev) => <AgendaRow key={ev.event_id} ev={ev} section={section} onOpenCase={onOpenCase} onChanged={onChanged} />)}
      </ul>
      {evs.length > shown.length && (
        <button type="button" onClick={() => setShowAll(true)} className="w-full py-1.5 text-[11px] text-sky-700 hover:bg-sky-50 border-b" data-testid={`lcal-agenda-more-${section}-${owner}`}>
          Show {evs.length - shown.length} more
        </button>
      )}
    </div>
  );
}

function AgendaRow({ ev, section, onOpenCase, onChanged }) {
  const [rediarise, setRediarise] = useState(false);
  const { isAdmin } = useAuth();
  const overdue = section === "overdue";
  return (
    <li className="px-3 py-2 hover:bg-slate-50 flex items-start gap-3" data-testid={`lcal-agenda-event-${ev.event_id}`}>
      <div className={`w-[86px] shrink-0 text-[11px] tabular-nums pt-0.5 ${overdue ? "text-red-700 font-semibold" : "text-slate-600"}`}>
        {dayLabel(ev.date)}
        {overdue && ev.days_overdue > 0 && <div className="text-[10px]">{ev.days_overdue}d overdue</div>}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 flex-wrap">
          <PriorityDot priority={ev.priority} daysOverdue={ev.days_overdue} />
          <span className={`text-[11px] px-1.5 py-0.5 rounded border font-medium ${TONE_CLASS[ev.tone] || TONE_CLASS.slate}`}>{ev.label}</span>
          {ev.mandatory && <span className="text-[9px] uppercase px-1 py-0.5 rounded border bg-rose-50 text-rose-700 border-rose-200" title="Mandatory — cannot be cancelled">mandatory</span>}
          {ev.sla_breached_at && <span className="text-[9px] uppercase px-1 py-0.5 rounded border bg-rose-600 text-white border-rose-700" title={`SLA deadline passed — ${ev.sla_days_late ?? 0} working day(s) late`} data-testid={`lcal-sla-badge-${ev.event_id}`}>SLA breached</span>}
          {ev.rediarise_count > 0 && <span className="text-[9px] text-slate-500" title="Re-diarised">↻×{ev.rediarise_count}</span>}
        </div>
        <div className="text-sm mt-0.5 flex items-center gap-2 min-w-0">
          <button type="button" className="font-medium truncate hover:underline text-left" onClick={() => ev.case_exists !== false && onOpenCase(ev.case_id)} data-testid={`lcal-agenda-open-${ev.event_id}`}>{ev.customer_name || "—"}</button>
          <span className="font-mono text-[11px] text-muted-foreground shrink-0">{ev.loan_application_id || ""}</span>
          <OwnerChip ev={ev} onChanged={onChanged} />
        </div>
        {ev.court?.name && <div className="text-[10px] text-slate-600 mt-0.5 flex items-center gap-1"><Landmark className="w-2.5 h-2.5" /> {courtSummary(ev.court)}</div>}
        {ev.notes && <div className="text-[11px] text-slate-600 mt-0.5 italic truncate" title={ev.notes}>“{ev.notes}”</div>}
      </div>
      <div className="flex items-center gap-1 shrink-0 flex-wrap justify-end max-w-[46%]">
        <EventActions ev={ev} onChanged={onChanged} />
        {ev.status === "scheduled" && !(ev.locked && !isAdmin()) && (
          <Button size="sm" variant="ghost" className="h-7 text-[11px] text-sky-700" onClick={() => setRediarise(true)} data-testid={`lcal-agenda-rediarise-${ev.event_id}`}>
            <CalIcon className="w-3 h-3 mr-1" /> Re-diarise
          </Button>
        )}
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onOpenCase(ev.case_id)} disabled={ev.case_exists === false} title="Open case">
          <ExternalLink className="w-3.5 h-3.5" />
        </Button>
      </div>
      {rediarise && <RediariseDialog ev={ev} onClose={() => setRediarise(false)} onDone={async () => { setRediarise(false); await onChanged?.(); }} />}
    </li>
  );
}
